import React, { useEffect, useMemo, useState } from 'react'
import { Form, Input, Select, Typography, Divider, Modal, Alert, Tooltip } from 'antd'
import { SearchOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { sapApi, approvalsApi } from '@/api/services'
import type { Approval } from '@/types'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import CustomFileUpload from './uploader/CustomFileUpload'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { Upload } from 'lucide-react'

const { Text } = Typography
const { Option } = Select

interface UploadFormData {
  documentNo: string
  type: string
}

function isSapErrorResponse(rd: any): boolean {
  if (!rd || typeof rd !== 'object') return false
  const status = String(rd.Status || rd.status || '').trim().toLowerCase()
  const message = String(rd.Message || rd.message || '').trim().toLowerCase()

  if (status === 'error' || status === 'e') return true
  if (message.includes('not found') || message.includes('document not found')) return true
  return false
}

const UploadPODPage: React.FC = () => {
  const { user } = useAuthStore()
  const canUpload = user?.permissions?.includes('pod.upload') ?? false
  const [form] = Form.useForm()
  const [selectedDocument, setSelectedDocument] = useState<any>(null)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [allowLongDocNo, setAllowLongDocNo] = useState<boolean>(false)
  const { t } = useTranslation(['UploadPOD', 'Common'])

  const { data: documentTypes } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => sapApi.getDocumentTypes(),
    // Backend responds `{ success, data: DocumentType[] }` but keep this resilient.
    select: (response: any) => {
      const payload = response?.data
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      return []
    },
  })

  const createApprovalMutation = useMutation({
    mutationFn: approvalsApi.createApproval,
    onSuccess: () => {
      toast.success(t('UploadPOD:messages.uploadSuccess', 'Document uploaded successfully!'))
      form.resetFields()
      setUploadedFiles([])
      setSelectedDocument(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('UploadPOD:messages.uploadFailed', 'Upload failed'))
    },
  })

  const fetchDocumentMutation = useMutation({
    mutationFn: sapApi.getDocumentLegacy,
    // axios returns AxiosResponse; API body lives in response.data
    onSuccess: (axiosResponse) => {
      const body = axiosResponse?.data ?? axiosResponse
      const rd = body?.data?.Response_Data ?? (body?.data as any)?.data?.Response_Data ?? body?.Response_Data

      if (isSapErrorResponse(rd)) {
        setSelectedDocument(null)
        toast.error(t('UploadPOD:messages.docNotFoundInSap', 'Document Not Found in SAP'))
      } else if (body?.success && rd) {
        setSelectedDocument(body)
        const docStatus = String(body?.document?.status || '').toLowerCase()
        if (docStatus === 'approved') {
          toast.warning(
            t(
              'UploadPOD:messages.alreadyApprovedWarning',
              'Document Already Uploaded and status "Approved" still you can upload document'
            )
          )
        } else {
          toast.success(t('UploadPOD:messages.fetchSuccess', 'Document details fetched from SAP'))
        }
      } else {
        setSelectedDocument(null)
        toast.error((body as any)?.error || t('UploadPOD:messages.fetchFailed', 'Failed to fetch document from SAP'))
      }
    },
    onError: (error: any) => {
      setSelectedDocument(null)
      toast.error(error.response?.data?.error || t('UploadPOD:messages.fetchFailed', 'Failed to fetch document from SAP'))
    },
  })

  const handleFetchDocument = () => {
    const documentNo = form.getFieldValue('documentNo')
    const type = form.getFieldValue('type')

    if (!documentNo || !type) {
      toast.error(t('UploadPOD:messages.validationError', 'Please enter document number and select document type'))
      return
    }

    setSelectedDocument(null)
    fetchDocumentMutation.mutate({
      Request_Data: { DocNo: String(documentNo), DocType: String(type) },
    })
  }

  // Backend: { success, data: { Response_Data: {...} }, document }
  const sapResponse =
    selectedDocument?.data?.Response_Data ?? (selectedDocument?.data as any)?.data?.Response_Data
  const existingDoc: Approval | null = selectedDocument?.document ?? null

  const sapFieldEntries = useMemo(() => {
    if (!sapResponse || typeof sapResponse !== 'object' || Array.isArray(sapResponse)) return []
    return Object.keys(sapResponse as object)
      .sort((a, b) => a.localeCompare(b))
      .map((k) => [k, (sapResponse as Record<string, unknown>)[k]] as const)
  }, [sapResponse])

  function formatSapFieldValue(value: unknown): string {
    if (value == null || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const mergeFilesToPdf = async (files: File[]) => {
    const { PDFDocument } = await import('pdf-lib')
    const pdfDoc = await PDFDocument.create()
    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!ext || !['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) continue
      const buf = await file.arrayBuffer()
      if (ext === 'png') {
        const img = await pdfDoc.embedPng(buf)
        const page = pdfDoc.addPage([img.width, img.height])
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height })
      } else if (ext === 'jpg' || ext === 'jpeg') {
        const img = await pdfDoc.embedJpg(buf)
        const page = pdfDoc.addPage([img.width, img.height])
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height })
      } else {
        const src = await PDFDocument.load(buf)
        const pages = await pdfDoc.copyPages(src, src.getPageIndices())
        pages.forEach((p) => pdfDoc.addPage(p))
      }
    }
    const merged = await pdfDoc.save()
    return new File([merged as any], `merged_${Date.now()}.pdf`, { type: 'application/pdf' })
  }

  const validateFileSize = (file: File, maxSizeMB = 25) => file.size / (1024 * 1024) <= maxSizeMB

  const createPayload = (mergedFile: File, values: UploadFormData) => {
    const d = sapResponse || {}
    const materialDocNo = d?.MaterialCodeNo || d?.MaterialDocNo || ''
    const lrNo = d?.LRNo || d?.LR_No || d?.['LR No'] || ''
    const doctype = d?.DocType || d?.Type || existingDoc?.type || values.type || ''
    const docNo = d?.DocumentNo || d?.DocNo || values.documentNo || ''

    const fd = new FormData()
    fd.append('year', d?.Year || '')
    fd.append('postingDate', d?.PostingDate || d?.Posting_Date || d?.BillingDate || d?.Billing_Date || '')
    fd.append('fromLocation', d?.FromLocation || '')
    fd.append('toLocation', d?.ToLocation || '')
    fd.append('status', 'Pending')
    fd.append('file', mergedFile, mergedFile.name)
    fd.append('type', String(doctype))
    fd.append('documentNo', String(docNo))
    fd.append('materialCodeNo', String(materialDocNo || ''))
    fd.append('quantity', String((d?.Quantity || '').toString().trim() || ''))
    fd.append(
      'actual_qty',
      String(d?.ActualQty ?? d?.Actual_Qty ?? d?.actual_qty ?? '').trim()
    )
    fd.append('excess_qty', String(d?.ExcessQty ?? d?.Excess_Qty ?? d?.excess_qty ?? '').trim())
    fd.append('short_qty', String(d?.ShortQty ?? d?.Short_Qty ?? d?.short_qty ?? '').trim())
    fd.append('vehicleNo', d?.VehicleNo || '')
    fd.append('lrNo', lrNo)
    fd.append('customerCode', d?.CustomerCode || '')
    fd.append('customerName', d?.CustomerName || '')
    fd.append('transporterCode', d?.Transporter || '')
    fd.append('transporterName', d?.TransporterName || '')
    fd.append('app', 'LEAP')
    // ensure current inputs win if SAP didn't provide
    fd.set('documentNo', String(values.documentNo))
    fd.set('type', String(values.type))
    fd.append('sapResponseJson', JSON.stringify(d))
    return fd
  }

  const handleSubmit = async () => {
    try {
      const values = (await form.validateFields()) as UploadFormData
      if (!uploadedFiles.length) {
        Modal.error({
          title: t('UploadPOD:errors.noFilesTitle', 'No Files'),
          content: t('UploadPOD:errors.noFilesContent', 'Please upload at least one file.')
        })
        return
      }
      if (!sapResponse || isSapErrorResponse(sapResponse)) {
        Modal.error({
          title: t('UploadPOD:errors.noDocTitle', 'No Document'),
          content: isSapErrorResponse(sapResponse)
            ? t('UploadPOD:messages.docNotFoundInSap', 'Document Not Found in SAP')
            : t('UploadPOD:errors.noDocContent', 'Please fetch a document from SAP first.')
        })
        return
      }
      const mergedFile = await mergeFilesToPdf(uploadedFiles)
      if (!validateFileSize(mergedFile)) {
        Modal.error({
          title: t('UploadPOD:errors.fileSizeTitle', 'File Size Error'),
          content: t('UploadPOD:errors.fileSizeContent', 'Attachments cannot exceed 25 MB.')
        })
        return
      }
      const formData = createPayload(mergedFile, values)
      createApprovalMutation.mutate(formData)
    } catch {
      // validation
    }
  }

  useEffect(() => {
    if (selectedDocument?.success && !sapResponse) {
      toast.error(t('UploadPOD:messages.noDetails', 'SAP did not return document details.'))
    }
  }, [sapResponse, selectedDocument?.success, t])

  return (
    <PageShell>
      <PageHero
        title={t('UploadPOD:header.title', 'Upload POD')}
        subtitle={t('UploadPOD:header.description', 'Select type + document no, fetch from SAP, attach files, upload.')}
        badges={[{ label: 'Upload', accent: true, icon: Upload }]}
      />

      {!canUpload && (
        <Alert
          type="info"
          showIcon
          message="View-Only Mode"
          description="You have read-only access. Fetching from SAP and uploading documents is restricted."
          className="mb-4"
        />
      )}

      <DashboardPanel
        title={t('UploadPOD:fetch.title', 'Fetch from SAP')}
        subtitle={t('UploadPOD:fetch.subtitle', 'Only document type + number are required')}
      >
        <div className="space-y-4">
          <Form form={form} layout="vertical" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="type"
              label={t('UploadPOD:form.docType', 'Document Type')}
              rules={[{ required: true, message: t('UploadPOD:form.docTypeRequired', 'Please select document type') }]}
            >
              <Select placeholder={t('UploadPOD:form.docTypePlaceholder', 'Select document type')}>
                {(Array.isArray(documentTypes) ? documentTypes : []).map((type) => (
                  <Option key={type.code} value={type.code} disabled={type.disabled}>
                    {t(`UploadPOD:types.${type.code}`, type.name) as string}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="documentNo"
              label={t('UploadPOD:form.docNo', 'Document Number')}
              extra={
                <div className="mt-1 text-xs font-medium flex items-center gap-1.5">
                  {allowLongDocNo ? (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <UnlockOutlined className="text-xs" />
                      {t('UploadPOD:form.docNoLongActive', 'Extended document number mode active — you can enter more than 10 digits.')}
                    </span>
                  ) : (
                    <span className="text-amber-600 flex items-center gap-1">
                      <LockOutlined className="text-xs" />
                      {t('UploadPOD:form.docNoLimitWarning', 'Accepts 10 digits by default. Want to enter a longer number? Click the lock icon to enable.')}
                    </span>
                  )}
                </div>
              }
              rules={[
                { required: true, message: t('UploadPOD:form.docNoRequired', 'Please enter document number') },
                ...(allowLongDocNo
                  ? []
                  : [{ max: 10, message: t('UploadPOD:form.docNoMax', 'Document number cannot exceed 10 digits unless unlocked') }]),
              ]}
            >
              <Input
                maxLength={allowLongDocNo ? undefined : 10}
                placeholder={t('UploadPOD:form.docNoPlaceholder', 'Enter document number')}
                suffix={
                  <div className="flex items-center gap-1.5">
                    <Tooltip
                      title={
                        allowLongDocNo
                          ? t('UploadPOD:form.lockTooltip', 'Lock to 10 digits maximum')
                          : t('UploadPOD:form.unlockTooltip', 'Click to permit more than 10 digits')
                      }
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const next = !allowLongDocNo
                          setAllowLongDocNo(next)
                          if (next) {
                            toast.info(t('UploadPOD:messages.unlockedLongDocNo', 'Unlocked: You can now enter document numbers longer than 10 digits.'))
                          } else {
                            toast.info(t('UploadPOD:messages.lockedDocNo', 'Restricted: Document number entry limited to 10 digits.'))
                          }
                        }}
                        className={`p-1 rounded transition-colors flex items-center justify-center ${
                          allowLongDocNo
                            ? 'text-emerald-600 hover:bg-emerald-50 bg-emerald-50/50'
                            : 'text-amber-600 hover:bg-amber-50 bg-amber-50/50'
                        }`}
                      >
                        {allowLongDocNo ? <UnlockOutlined className="text-sm" /> : <LockOutlined className="text-sm" />}
                      </button>
                    </Tooltip>
                    <SearchOutlined className="text-muted-foreground" />
                  </div>
                }
              />
            </Form.Item>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleFetchDocument}
              disabled={!canUpload || fetchDocumentMutation.isPending}
            >
              {fetchDocumentMutation.isPending ? t('UploadPOD:form.fetching', 'Fetching…') : t('UploadPOD:form.fetch', 'Fetch')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.resetFields()
                setSelectedDocument(null)
                setUploadedFiles([])
                setAllowLongDocNo(false)
              }}
            >
              {t('Common:general.reset', 'Reset')}
            </Button>
          </div>
          </Form>

          <Divider />

          <DashboardPanel
            title={t('UploadPOD:details.title', 'Fetched Details')}
            subtitle={t('UploadPOD:details.subtitle', 'Read-only (from SAP)')}
            className="shadow-kpi border-border/80"
          >
            {sapResponse ? (
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {sapFieldEntries.map(([key, val]) => (
                  <div key={key}>
                    <Text type="secondary">{t(`UploadPOD:details.fields.${key}`, key)}</Text>
                    <div className="break-words text-foreground">{formatSapFieldValue(val)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t('UploadPOD:details.empty', 'Fetch a document to see details.')}</div>
            )}
            {existingDoc ? (
              existingDoc.status?.toLowerCase() === 'approved' ? (
                <Alert
                  type="warning"
                  showIcon
                  className="mt-4 border-amber-300 bg-amber-50 text-amber-900"
                  message={
                    <span className="font-medium">
                      {t(
                        'UploadPOD:details.alreadyApprovedWarning',
                        'Document Already Uploaded and status "Approved" still you can upload document'
                      )}
                    </span>
                  }
                />
              ) : (
                <div className="mt-4 text-xs text-muted-foreground">
                  {t(
                    'UploadPOD:details.existingAttachment',
                    'Existing attachment found in system (latest status: {{status}}). You can still upload a new attachment.',
                    { status: existingDoc.status }
                  )}
                </div>
              )
            ) : null}
          </DashboardPanel>

          <Divider />

          <DashboardPanel
            title={t('UploadPOD:upload.title', 'Attachments')}
            subtitle={t('UploadPOD:upload.description', 'Upload JPG/PNG/PDF. Multiple files will be merged into one PDF. Max 25MB.')}
            className="shadow-kpi border-border/80"
          >
            {canUpload ? (
              <CustomFileUpload uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles} multiple acceptedFileTypes=".jpg,.jpeg,.png,.pdf" />
            ) : (
              <div className="text-sm text-muted-foreground p-4 bg-muted/20 rounded-md border border-dashed border-border text-center">
                File upload is disabled in view-only mode.
              </div>
            )}
          </DashboardPanel>

          {canUpload && (
            <div className="flex justify-end">
              <Button type="button" onClick={handleSubmit} disabled={createApprovalMutation.isPending}>
                {createApprovalMutation.isPending ? t('UploadPOD:upload.uploading', 'Uploading…') : t('UploadPOD:upload.submit', 'Upload')}
              </Button>
            </div>
          )}
        </div>
      </DashboardPanel>
    </PageShell>
  )
}

export default UploadPODPage
